import React, { useState } from 'react';
import { PlusIcon, PhotographIcon, TrashIcon, CheckCircleIcon } from '@heroicons/react/outline';

const CompletedQuestionsList = ({ questions, onDeleteQuestion }) => {
  // دالة لتقصير النص إلى 40 حرف
  const truncateText = (text, maxLength = 40) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // تصفية الأسئلة لاستبعاد الأسئلة المحذوفة وترتيبها من الأحدث للأقدم
  const activeQuestions = [...questions]
    .filter(question => !question.deleted && !question.is_deleted)
    .sort((a, b) => (a.order || 0) - (b.order || 0));
  
  // عدد الأسئلة الأصلية: questions.length
  // عدد الأسئلة النشطة: activeQuestions.length
  
  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-4">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">الأسئلة المكتملة</h3>
      <div className="space-y-3">
        {activeQuestions.map((question, index) => (
          <div
            key={question.id || question.db_id || index}
            className="p-3 bg-gray-50 dark:bg-gray-700 rounded-lg"
          >
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <CheckCircleIcon className="h-5 w-5 text-green-500" />
                <span className="text-sm font-medium text-gray-900 dark:text-white">
                  {truncateText(question.text)}
                </span>
              </div>
              <button 
                onClick={() => {
                  onDeleteQuestion(question);
                }}
                className="text-red-500 hover:text-red-700 focus:outline-none"
                title="حذف السؤال"
              >
                <TrashIcon className="h-5 w-5" />
              </button>
            </div>
            {/* تم إخفاء عرض الإجابة الصحيحة بناءً على طلب العميل */}
          </div>
        ))}
      </div>
    </div>
  );
};

const QuestionForm = ({ onSave, totalScore, numQuestions, examId, onDirectSave }) => {
  const [question, setQuestion] = useState({
    id: Date.now(),
    text: '',
    score: 0,
    image: null,
    notification: '',
    answers: Array(4).fill(null).map((_, idx) => ({
      id: Date.now() + idx + 1,
      text: '',
      isCorrect: idx === 0,
      image: null
    }))
  });

  const handleImageUpload = (event, type, answerIndex = null) => {
    const file = event.target.files[0];
    if (!file) return;

    // تخزين الملف نفسه بدلاً من base64
    if (type === 'question') {
      setQuestion(prev => ({ 
        ...prev, 
        image: file,
        imagePreview: URL.createObjectURL(file) // لعرض معاينة الصورة
      }));
    } else if (type === 'answer' && answerIndex !== null) {
      setQuestion(prev => ({
        ...prev,
        answers: prev.answers.map((a, idx) =>
          idx === answerIndex ? { 
            ...a, 
            image: file,
            imagePreview: URL.createObjectURL(file) // لعرض معاينة الصورة
          } : a
        )
      }));
    }
  };

  const [isSaving, setIsSaving] = useState(false);

  const handleSave = async () => {
    // فلترة الإجابات الفارغة
    const validAnswers = question.answers
      .filter(a => a.text && a.text.trim() !== '')
    
    if (question.text && validAnswers.length >= 2) {
      setIsSaving(true);
      try {
        const score = calculateScore(totalScore, numQuestions);
        
        // إنشاء نسخة من السؤال مع الإجابات المفلترة
        const questionToSave = {
          ...question,
          score,
          answers: validAnswers,
          imagePreview: undefined
        };
        
        // تعديل الإجابات لحذف imagePreview
        questionToSave.answers = validAnswers.map(answer => ({
          ...answer,
          imagePreview: undefined
        }));
        
        // استدعاء الدالة الرئيسية لإضافة السؤال
        onSave(questionToSave);
        
        // إعادة تعيين النموذج
        setQuestion({
          id: Date.now(),
          text: '',
          score: 0,
          image: null,
          imagePreview: null,
          notification: '',
          answers: Array(4).fill(null).map((_, idx) => ({
            id: Date.now() + idx + 1,
            text: '',
            isCorrect: idx === 0,
            image: null,
            imagePreview: null
          }))
        });
      } catch (error) {
        // معالجة خطأ حفظ السؤال
        // يمكن إضافة رسالة خطأ هنا إذا كان لديك خدمة للرسائل
      } finally {
        setIsSaving(false);
      }
    }
  };

  const calculateScore = (totalScore, numQuestions) => {
    return totalScore / numQuestions;
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-6">إضافة سؤال جديد</h3>
      
      <div className="space-y-6">
        {/* نص السؤال مع الصورة */}
        <div className="flex gap-4">
          <div className="flex-1">
            <input
              type="text"
              value={question.text}
              onChange={(e) => setQuestion(prev => ({ ...prev, text: e.target.value }))}
              placeholder="نص السؤال"
              className="input-base w-full"
            />
          </div>
          <div className="flex-shrink-0">
            <label className="block">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 cursor-pointer transition-colors">
                {question.imagePreview ? (
                  <img
                    src={question.imagePreview}
                    alt=""
                    className="w-full h-full object-cover rounded-lg"
                  />
                ) : (
                  <PhotographIcon className="h-6 w-6 text-gray-400" />
                )}
              </div>
              <input
                type="file"
                className="hidden"
                accept="image/*"
                onChange={(e) => handleImageUpload(e, 'question')}
              />
            </label>
          </div>
        </div>

        {/* حقل التنويه الاختياري */}
        <div>
          <textarea
            value={question.notification}
            onChange={(e) => setQuestion(prev => ({ ...prev, notification: e.target.value }))}
            placeholder="تنويه للإجابة الخاطئة (اختياري) - سيظهر للمستخدم عند الإجابة الخاطئة"
            className="input-base w-full h-20 resize-none"
            rows={3}
          />
        </div>

        {/* الإجابات */}
        <div className="grid grid-cols-1 gap-4">
          {question.answers.map((answer, answerIndex) => (
            <div key={answer.id} className="flex items-center gap-4">
              <input
                type="radio"
                name="correct-answer"
                checked={answer.isCorrect}
                onChange={() => {
                  setQuestion(prev => ({
                    ...prev,
                    answers: prev.answers.map((a, idx) => ({
                      ...a,
                      isCorrect: idx === answerIndex
                    }))
                  }));
                }}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div className="flex-1">
                <input
                  type="text"
                  value={answer.text}
                  onChange={(e) => {
                    setQuestion(prev => ({
                      ...prev,
                      answers: prev.answers.map((a, idx) =>
                        idx === answerIndex ? { ...a, text: e.target.value } : a
                      )
                    }));
                  }}
                  placeholder={`الإجابة ${answerIndex + 1}`}
                  className="input-base w-full"
                />
              </div>
              <label className="block">
                <div className="w-10 h-10 flex items-center justify-center rounded-lg border-2 border-dashed border-gray-300 hover:border-blue-500 cursor-pointer transition-colors">
                  {answer.imagePreview ? (
                    <img
                      src={answer.imagePreview}
                      alt=""
                      className="w-full h-full object-cover rounded-lg"
                    />
                  ) : (
                    <PhotographIcon className="h-6 w-6 text-gray-400" />
                  )}
                </div>
                <input
                  type="file"
                  className="hidden"
                  accept="image/*"
                  onChange={(e) => handleImageUpload(e, 'answer', answerIndex)}
                />
              </label>
            </div>
          ))}
        </div>

        {/* زر الحفظ */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleSave}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 focus:outline-none flex items-center justify-center min-w-[120px] transition-colors duration-200"
            disabled={isSaving}
          >
            {isSaving ? (
              <>
                <span className="mr-2 spinner-border spinner-border-sm" role="status" aria-hidden="true"></span>
                جاري الحفظ...
              </>
            ) : (
              'حفظ السؤال'
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

const ExamQuestions = ({ questions, onAddQuestion, onUpdateQuestion, onDeleteQuestion, examId, onDirectSave, totalScore }) => {
  const handleAddQuestion = (newQuestion) => {
    // نمرر السؤال الجديد ككائن منفرد، وستقوم الدالة الأصلية بإضافته للقائمة
    onAddQuestion(newQuestion);
  };

  const handleDeleteQuestionLocal = async (question) => {
    const index = questions.findIndex(q => 
      (q.id && q.id === question.id) || 
      (q.tempId && q.tempId === question.tempId) ||
      (q.db_id && q.db_id === question.db_id)
    );
    if (index !== -1) {
      const questionId = question.db_id || question.id;
      await onDeleteQuestion(index, questionId);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-semibold text-gray-900 dark:text-white">الأسئلة</h2>
      </div>

      <div className="grid grid-cols-2 gap-6">
        {/* نموذج إضافة سؤال جديد */}
        <div>
          <QuestionForm 
            onSave={handleAddQuestion} 
            totalScore={totalScore}
            numQuestions={questions.length + 1}
            examId={examId}
            onDirectSave={onDirectSave}
          />  
        </div>

        {/* قائمة الأسئلة المكتملة */}
        <div>
          <CompletedQuestionsList 
            questions={questions} 
            onDeleteQuestion={handleDeleteQuestionLocal}
          />
        </div>
      </div>
    </div>
  );
};

export default ExamQuestions;
